/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';

import { Theme } from 'features/Theme';
import Button, { ButtonProps } from 'components/Button';
import useTwoWayHybridApplyEcho from 'hooks/requests/twoWayHybridApplyEcho';
import useTranslate from 'hooks/useTranslate';
import { FC, useCallback } from 'react';
import { selectTenant } from 'state/tenant';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { selectInstantCredit } from 'state/instantCredit';

interface PropTypes extends ButtonProps<'button'> {
  callId?: string;
}

const returnValues = new Map<string, string>([
  ['01', 'Approval'],
  ['02', 'Investigation'],
  ['03', 'Processing Error'],
  ['04', 'Time-Out'],
  ['06', 'Existing Account'],
  ['07', 'Invalid SSN'],
  ['09', 'Declined'],
  ['21', 'Second Source approval'],
  ['29', 'Second Source denial'],
]);

const TwoWayHybridButton: FC<PropTypes> = props => {
  const theme = useTheme<Theme>();
  const {
    enabledFeatures: { twoWayHybridApplyEcho },
  } = useSelector(selectTenant);

  const { returnCode, isApproved, hasExistingAccount } = useSelector(
    selectInstantCredit,
  );
  const [sendTwoWayHybridApplyEcho] = useTwoWayHybridApplyEcho();

  const t = useTranslate();

  const { sendEvents } = useAnalytics();

  const onPress = useCallback(() => {
    if (!twoWayHybridApplyEcho) {
      return;
    }

    sendEvents([Event.ApplyAndBuy]);

    let statusDescription: string;
    if (hasExistingAccount) {
      statusDescription = 'Existing Account';
    } else {
      statusDescription = returnCode ? returnValues.get(returnCode) || '' : '';
    }

    sendTwoWayHybridApplyEcho(
      returnCode || '',
      Boolean(isApproved),
      statusDescription,
    );
  }, [
    hasExistingAccount,
    isApproved,
    returnCode,
    sendEvents,
    sendTwoWayHybridApplyEcho,
    twoWayHybridApplyEcho,
  ]);

  if (!twoWayHybridApplyEcho || theme.embedded) {
    return null;
  }

  return (
    <Button onClick={onPress} {...props}>
      {t('dsp.offer.shopButton')}
    </Button>
  );
};

export default TwoWayHybridButton;
